import React from "react";
import { t } from "i18next";
import { getRoundTitle, getOrdinalSuffix } from "../../../constants";
import { getMatchParticipantNames, getMatchScoreText } from "./exportUtils";
import { PRINT_THEME as T } from "./printTheme";

const BORDER = T.border;

// Row-per-player table with rowSpan-merged match/winner cells, ported from
// this app's own live "Pro" bracket (draws/Bracket.js bodyRows/headerCells) -
// same row-index math (matchStart/blockSize/mRow/span), stripped of all
// interactivity (drag/drop, touch, seed editing, participant menus) and
// re-themed with PRINT_THEME literals instead of CSS vars, since this is a
// plain white page (html2canvas/pdf capture), not the live app document.
// getRs() in Bracket.js exists only to compensate rowSpan for the extra gap
// <tr> it inserts between matches for breathing room - this version has no
// gap rows, so every rowSpan below is used as-is (span, half-span, or 1).
const buildLayout = (draw) => {
    const byNum = {};
    const round1Matches = [];
    (draw.matches || []).forEach(m => {
        byNum[m.match_number] = m;
        if (m.round_number === 1) round1Matches.push(m);
    });
    round1Matches.sort((a, b) => (a.match_number || 0) - (b.match_number || 0));

    const nums = (draw.matches || []).map(m => m.match_number);
    const matchOffset = nums.length ? Math.min(...nums) - 1 : 0;

    const numberPlayers = draw.number_players || round1Matches.length * 2;
    const N = Math.pow(2, Math.ceil(Math.log2(Math.max(2, numberPlayers))));
    const R = Math.log2(N);
    const matchStart = r => matchOffset + N - (N >> (r - 1));
    const showSeedColumn = round1Matches.some(m => m.seed1 || m.seed2);

    return { byNum, round1Matches, N, R, matchStart, showSeedColumn };
};

const cellText = (grade) => (match, side, isFirstRound) => {
    if (!match) return { text: "", isBye: false, isWinner: false, isLoser: false };
    const isBye = side === 1 ? match.x1 : match.x2;
    if (isBye) return { text: t("Bye"), isBye: true, isWinner: false, isLoser: false };

    const { side1, side2 } = getMatchParticipantNames(match, grade);
    const opponentIsBye = side === 1 ? match.x2 : match.x1;
    const isWinner = match.winner_side === side || opponentIsBye;
    const isLoser = match.winner_side != null && match.winner_side !== side;
    const seed = side === 1 ? match.seed1 : match.seed2;
    const seedPrefix = (isFirstRound && seed) ? `(${seed}) ` : "";

    return { text: `${seedPrefix}${side === 1 ? side1 : side2}`, isBye: false, isWinner, isLoser };
};

const nameStyle = ({ isWinner, isLoser, isBye }) => ({
    fontSize: "11px",
    fontWeight: isWinner ? "700" : "500",
    fontStyle: isBye ? "italic" : "normal",
    color: isWinner ? T.primaryGreen : (isLoser || isBye) ? T.muted : T.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const SingleBracketTable = ({ draw, grade, i18n }) => {
    const { byNum, round1Matches, N, R, matchStart, showSeedColumn } = buildLayout(draw);
    if (!R) return null;
    const display = cellText(grade);

    const td = (extra = {}) => ({
        padding: "4px 8px",
        borderBottom: `1px solid ${BORDER}`,
        boxSizing: "border-box",
        ...extra,
    });

    const headerCell = (label, extra = {}) => (
        <td style={{
            background: "transparent", color: T.ink, fontSize: "11px", fontWeight: "500",
            textTransform: "uppercase", textAlign: "center",
            padding: "10px 5px", whiteSpace: "nowrap", borderBottom: "none", ...extra,
        }}>
            {label}
        </td>
    );

    // No horizontal rules anywhere in the body - match grouping comes only
    // from the round-1 zebra background (matchBg below) and the round
    // columns' own border-left connectors, not from lines.

    const rows = Array.from({ length: N }, (_, i) => {
        const matchIdx = i >> 1;
        const side = i % 2 === 0 ? 1 : 2;
        const r1Match = round1Matches[matchIdx];
        // Zebra by match (both of a match's two rows share a color), not by
        // row - a per-row stripe would cut a match's own pair in half instead
        // of grouping it, which is what a line between matches used to do.
        const matchBg = matchIdx % 2 === 0 ? T.white : T.superWhite;
        const cells = [];

        cells.push(
            <td key="n" style={td({ textAlign: "center", color: T.muted, fontSize: "10px", width: "22px", borderBottom: "none", background: matchBg })}>
                {i + 1}
            </td>
        );

        if (showSeedColumn) {
            const seed = r1Match ? (side === 1 ? r1Match.seed1 : r1Match.seed2) : "";
            cells.push(
                <td key="seed" style={td({ textAlign: "center", color: T.muted, fontSize: "10px", width: "24px", borderBottom: "none", background: matchBg })}>
                    {seed || ""}
                </td>
            );
        }

        const p1 = display(r1Match, side, true);
        cells.push(
            <td key="player" style={td({ minWidth: "150px", borderBottom: "none", background: matchBg })}>
                <span style={nameStyle(p1)}>{p1.text}</span>
            </td>
        );

        for (let r = 1; r <= R; r++) {
            const blockSize = 1 << r;
            const k = i >> r;
            const blockStart = k * blockSize;
            const mRow = blockStart + (blockSize >> 1) - 1;
            const matchNum = matchStart(r) + k + 1;
            const span = Math.max(2, 1 << (r - 1));
            const halfSpan = span >> 1;
            const startRow = mRow - halfSpan + 1;
            const inSpan = i >= startRow && i < startRow + span;

            // Bracket.js only skips border-left on round 1's match-number column
            // because round 1 already has a hard edge there from the player
            // column right next to it - here that column has no border of its
            // own on that side, so without this the round-1 connector reads as
            // cut short right at the match number instead of running the same
            // full length every other round's connector does.
            if (i === startRow) {
                cells.push(
                    <td key={`m${r}`} rowSpan={halfSpan} style={{ textAlign: "left", verticalAlign: "bottom", borderLeft: `1px solid ${BORDER}`, borderBottom: "none", width: "26px", padding: "2px" }}>
                        <span style={{ fontSize: "9px", color: T.muted }}>#{matchNum}</span>
                    </td>
                );
            } else if (i === mRow + 1) {
                cells.push(<td key={`mb${r}`} rowSpan={halfSpan} style={{ borderLeft: `1px solid ${BORDER}`, borderBottom: "none", width: "26px", padding: "2px" }} />);
            }

            const currentMatch = byNum[matchNum];

            if (i === mRow) {
                const nextMatchNum = r < R ? matchStart(r + 1) + (k >> 1) + 1 : null;
                const nextMatch = nextMatchNum ? byNum[nextMatchNum] : null;
                const nextSide = (k & 1) === 0 ? 1 : 2;
                let content = { text: "", isBye: false, isWinner: false, isLoser: false };
                if (nextMatch) content = display(nextMatch, nextSide, false);
                else if (currentMatch?.winner_side) content = display(currentMatch, currentMatch.winner_side, false);
                else if (currentMatch?.x1 && currentMatch?.x2) content = { text: t("Bye"), isBye: true };
                else if (currentMatch?.x1) content = display(currentMatch, 2, false);
                else if (currentMatch?.x2) content = display(currentMatch, 1, false);

                cells.push(
                    <td key={`w${r}`} style={{ minWidth: "150px", padding: "4px 8px", verticalAlign: "bottom", borderBottom: `1px solid ${BORDER}` }}>
                        <span style={nameStyle(content)}>{content.text}</span>
                    </td>
                );
            } else if (i === mRow + 1) {
                const scoreText = getMatchScoreText(currentMatch);
                cells.push(
                    <td key={`s${r}`} style={{ minWidth: "150px", padding: "0 8px 4px", verticalAlign: "top", borderBottom: "none", fontSize: "10px", color: T.muted }}>
                        {scoreText}
                    </td>
                );
            } else if (inSpan) {
                cells.push(<td key={`ew${r}`} style={{ border: "none" }} />);
            } else {
                cells.push(<td key={`em${r}`} style={{ border: "none" }} />);
                cells.push(<td key={`ew${r}`} style={{ border: "none" }} />);
            }
        }

        cells.push(<td key="spacer" style={{ border: "none" }} />);

        return <tr key={i}>{cells}</tr>;
    });

    const headerCells = [
        headerCell("№", { width: "22px" }),
        ...(showSeedColumn ? [headerCell(t("S"), { width: "24px" })] : []),
        headerCell(getRoundTitle(1, R, t, i18n, null, false)),
        ...Array.from({ length: R }, (_, idx) => {
            const r = idx + 1;
            return [
                headerCell("", { width: "26px" }),
                headerCell(r < R ? getRoundTitle(r + 1, R, t, i18n, null, false) : `${draw.number}${getOrdinalSuffix(draw.number)} ${t("Place")}`),
            ];
        }).flat(),
        headerCell("", { width: "100%" }),
    ];

    return (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
                <tr>{headerCells}</tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>
    );
};

const PrintableBracketTree = ({ draws, grade, i18n }) => {
    const list = Array.isArray(draws) ? draws : (draws?.draws || []);
    if (!list.length) return null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "28px", width: "100%" }}>
            {list.map((draw, idx) => (
                <div key={draw.id || idx} style={{ border: `1px solid ${BORDER}`, borderRadius: "6px", overflow: "visible", background: T.white }}>
                    {/* Gray/white/uppercase - matches the vector PDF export's card
                        header bar exactly (pdfBracketTree.js); the PDF has no
                        green anywhere, this PNG path keeps its own green
                        winner-highlight color elsewhere, but the banner itself
                        stays identical between the two exports. */}
                    <div style={{ background: T.headerGray, color: T.white, padding: "8px 12px", fontWeight: "bold", fontSize: "14px", letterSpacing: "0.2px", textTransform: "uppercase" }}>
                        {draw.name || `${draw.number}${getOrdinalSuffix(draw.number)} ${t("Place")}`}
                    </div>
                    <div style={{ padding: "16px 24px 24px", overflowX: "auto" }}>
                        <SingleBracketTable draw={draw} grade={grade} i18n={i18n} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default PrintableBracketTree;
